// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { LiveAnnouncer } from '@angular/cdk/a11y';
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Deal, DealData, DealStatuses, JacketType } from '@interfaces/deal.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { User, UserPermissionIds } from '@interfaces/user.interfaces';
import { DealsService } from '@services/deals.service';
import { ErrorService } from '@services/error.service';
import { NavService } from '@services/nav.service';
import { PermissionsService } from '@services/permissions.service';
import { SnackbarService } from '@services/snackbar.service';
import { SuccessModalService } from '@services/success-modal.service';
import { UserService } from '@services/user.service';
import { catchError, takeWhile } from 'rxjs';
import { DeleteDealModalComponent } from 'src/app/modals/delete-deal-modal/delete-deal-modal.component';
import { DealsHelpPanelComponent } from 'src/app/shared/help-panels/deals-help/deals-help-panel.component';
import { scrollIntoView } from 'src/app/utils/utils';

@Component({
    selector: 'dealer-deals',
    templateUrl: './dealer-deals.component.html',
    styleUrls: ['./dealer-deals.component.scss'],
})
export class DealerDealsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('helpPanel') helpPanel: TemplateRef<DealsHelpPanelComponent>;
    @ViewChild(MatSort) sort: MatSort;
    public deals: Deal[] = [];
    public totalNumberOfDeals: number = null;
    public records = 25;
    private isComponentAlive = true;
    public isFiltered = false;
    public isLoading = false;
    public query = '';
    private selectedDealership: number = null;
    public user: User;
    displayedColumns: string[] = ['status', 'jacketId', 'dealNumber', 'customer', 'coBuyer', 'vehicle', 'createDate', 'actions'];
    public isTableView: boolean;
    public hasDealDocumentsDelete = false;
    public dataSource = new MatTableDataSource<Deal>([]);

    public readonly DealStatuses = DealStatuses;
    protected readonly JacketCategoryOptions = Object.entries(DealStatuses)
        .map(([key, value]) => ({ key, value: value as DealStatuses }))
        .filter((status) => !isNaN(status?.value));
    public selectedCategoryFilter: DealStatuses[] = [DealStatuses.Open];

    constructor(
        private readonly dealService: DealsService,
        private readonly navService: NavService,
        private readonly route: ActivatedRoute,
        private readonly permissionsService: PermissionsService,
        private readonly userService: UserService,
        private readonly errorService: ErrorService,
        private readonly snackbarService: SnackbarService,
        private readonly successModalService: SuccessModalService,
        private readonly _liveAnnouncer: LiveAnnouncer,
        private router: Router,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.deals = this.route.snapshot.data.deals.deals;
        this.dataSource.data = this.deals;

        const storedView = localStorage.getItem('isTableView');
        if (storedView !== null) {
            this.isTableView = JSON.parse(storedView);
        } else {
            this.isTableView = false;
            localStorage.setItem('isTableView', JSON.stringify(this.isTableView));
        }

        this.records = this.dealService.getTablePageSize(JacketType.Deal);

        this.totalNumberOfDeals = this.route.snapshot.data.deals.count;

        this.selectedDealership = this.route.snapshot.data.user.dealerId;
        this.navService.setTitle('Deals');

        const categoryFilter = this.userService.getFilterState<DealStatuses>(JacketType.Deal);

        this.userService
            .user$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                this.user = user;
            });

        if (categoryFilter) {
            this.selectedCategoryFilter = categoryFilter;
            this.selectDealType();
        }

        this.hasDealDocumentsDelete = this.hasDeletePermission;
    }

    // hides the tool tips on the paginator arrows if paginator is on the page
    ngAfterViewInit(): void {
        this.navService.setHelpPanel(this.helpPanel);
        if (this.paginator) {
            const paginatorIntl = this.paginator._intl;
            paginatorIntl.nextPageLabel = '';
            paginatorIntl.previousPageLabel = '';
        }

        this.dealershipChangedReloadDeals();
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'status':
                    return item.statusId;
                case 'jacketId':
                    return item.dealId;

                case 'customer':
                    return `${item.buyer?.firstName || ''} ${item.buyer?.lastName || ''}`.toLowerCase();
                case 'coBuyer':
                    return `${item.coBuyer?.firstName || ''} ${item.coBuyer?.lastName || ''}`.toLowerCase();
                case 'vehicle':
                    return `${item.vehicle?.year ?? ''} ${item.vehicle?.make ?? ''} ${item.vehicle?.model ?? ''}`.toLowerCase();
                case 'createDate':
                    return new Date(item?.createDate ?? 0).getTime();
                default:
                    return (item as any)[property];
            }
        };
    }

    get hasDeletePermission(): boolean {
        return this.permissionsService.hasPermission(UserPermissionIds.DealsDelete);
    }

    get hasCloseArchivePermission(): boolean {
        return this.permissionsService.hasPermission(UserPermissionIds.DealsFinalizeAndClose);
    }

    redirect(deal: Deal): void {
        const route = window.location.pathname.includes('pddj') ? ['/pddj', deal.dealId, 'upload'] : ['/dashboard', 'deals', deal.dealId];

        this.router.navigate(route);
    }

    canArchive(deal: Deal): boolean {
        if (deal && this.user) {
            return this.permissionsService.canCloseArchiveFinalizeDeal(deal.statusId, [DealStatuses.Completed], deal.jacketTypeId);
        } else {
            return false;
        }
    }

    canClose(deal: Deal): boolean {
        if (deal && this.user) {
            return this.permissionsService.canCloseArchiveFinalizeDeal(deal.statusId, [DealStatuses.Open, DealStatuses.Archived], deal.jacketTypeId);
        } else {
            return false;
        }
    }

    canReopen(deal: Deal): boolean {
        if (deal && this.user) {
            return true;
        } else {
            return false;
        }
    }

    canDeleteRequest(deal: Deal): boolean {
        return this.permissionsService.canDeleteDeal(deal.statusId, [DealStatuses.Open], deal.jacketTypeId);
    }

    canRestoreJacket(jacket: Deal, withStatus: boolean): boolean {
        if (withStatus) {
            return this.permissionsService.canDeleteDeal(jacket.statusId, [DealStatuses.Deleted], jacket.jacketTypeId);
        } else {
            return this.permissionsService.hasPermission(UserPermissionIds.DocumentStorageDelete);
        }
    }

    dealRestore(jacket: Deal) {
        this.isLoading = true;
        this.dealService
            .restoreDeal$(jacket.dealId, jacket.jacketTypeId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((deal: Deal) => {
                this.isLoading = false;
                const customerName = this.getCustomerName(deal);

                this.snackbarService.openSnackbar('success', `The Document Jacket for <strong>${customerName}</strong> has been restored.`);
                this.redirect(deal);
            });
    }

    dealDelete(deal: any): void {
        if (this.canDeleteRequest(deal)) {
            //Routing wants jacket Type as request to route back to consumer documents.
            deal.jacketType = 'request';
            this.dialog
                .open(DeleteDealModalComponent, {
                    width: '700px',
                    closeOnNavigation: false,
                    data: {
                        dealId: deal.dealId,
                        jacketTypeId: deal.jacketTypeId,
                        customerName: this.getCustomerName(deal),
                    },
                })
                .afterClosed()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((result) => {
                    this.selectDealType();

                    if (result) {
                        this.snackbarService.openSnackbar('success', `The jacket for <b>${this.getCustomerName(deal)}</b> has been deleted.`);
                    }
                });
        }
    }

    isCategoryType(categories: DealStatuses[], currentStatus: DealStatuses): boolean {
        return categories.some(
            (category) => (this.selectedCategoryFilter.includes(category) || this.selectedCategoryFilter.length === 0) && currentStatus === category
        );
    }

    displayCategory(category: string): string {
        if (category === 'Completed') return 'Closed';
        return category;
    }

    updateJacketStatus(deal: Deal) {
        // close deal
        if (this.isCategoryType([DealStatuses.Open, DealStatuses.Archived], deal.statusId)) {
            this.isLoading = true;
            this.dealService
                .closeDeal$(deal.dealId, deal.jacketTypeId, true)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.errorService.showGenericError();
                        this.isLoading = false;
                        throw error;
                    })
                )
                .subscribe((result) => {
                    this.selectDealType();

                    const customerName =
                        !deal?.buyer?.firstName && !deal?.buyer?.lastName ? 'the customer' : `${deal?.buyer?.firstName} ${deal?.buyer?.lastName}`;

                    this.snackbarService.openSnackbar('success', `The jacket for <b>${customerName}</b> has been moved to Closed.`);

                    this.isLoading = false;
                });
        }

        //Archive
        if (this.isCategoryType([DealStatuses.Completed], deal.statusId)) {
            this.isLoading = true;
            this.dealService
                .archiveDeal$(deal.dealId, deal.dealerId, deal.jacketTypeId, true)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.errorService.showGenericError();
                        this.isLoading = false;
                        throw error;
                    })
                )
                .subscribe((x) => {
                    this.selectDealType();
                    this.isLoading = false;
                });
        }
    }

    format(type: 'date', value: string) {
        switch (type) {
            case 'date':
                return new Date(value).toLocaleString();
            default:
                return value;
        }
    }

    reopenJacketStatus(deal: Deal) {
        this.isLoading = true;
        this.dealService
            .reopenDeal$(deal.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((result) => {
                this.selectDealType();

                const customerName = this.getCustomerName(deal);
                this.successModalService.showSuccess({
                    title: 'Document Jacket Moved to Open',
                    content: `The Document Jacket for ${customerName} has been moved to Open.`,
                    btnText: 'Okay',
                });

                this.isLoading = false;
            });
    }

    hasEllipsisMenuAccess(jacket: any): boolean {
        return jacket.statusId !== DealStatuses.Archived || this.hasCloseArchivePermission;
    }

    // listen to changes to dealerId on user object and reload deals if it changes
    private dealershipChangedReloadDeals(): void {
        this.userService
            .user$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user: User) => {
                if (this.selectedDealership !== user.dealerId) {
                    this.isLoading = true;
                    this.selectedDealership = user.dealerId;
                    this.getDeals();
                }
            });
    }

    public eventHandler(event: AppEvent): void {
        switch (event.context) {
            case 'search':
            case 'query':
                this.query = event.data;
                if (this.paginator) this.paginator.firstPage();
                this.searchDeals(event.data);
                break;
            default:
                break;
        }
    }

    filterHandler(event: MatSelectChange) {
        this.selectedCategoryFilter = event.value;
        this.userService.setFilterState(JacketType.Deal, this.selectedCategoryFilter);
        // reset the page back to 1 for new results
        if (this.paginator) {
            this.paginator.firstPage();
            this.onPageChange(null);
        } else {
            this.selectDealType(this.query);
        }
    }

    onToggleChanged(event: MatSlideToggleChange): void {
        this.isTableView = event.checked;
        localStorage.setItem('isTableView', JSON.stringify(this.isTableView));
    }

    // called when switching dealerships - we don't want to hold onto current query/paginations results
    private getDeals(): void {
        this.dealService
            .getDealsByType$(JacketType.Deal)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deals: DealData) => {
                this.deals = deals.deals;
                this.dataSource.data = this.deals;
                this.totalNumberOfDeals = deals.count;
                this.selectedCategoryFilter = [DealStatuses.Open];
                this.isLoading = false;
            });
    }

    public onPageChange(event: PageEvent): void {
        const offset = this.paginator?.pageIndex * this.paginator.pageSize;
        const pageSize = this?.paginator?.pageSize;
        const query: string = this.query;
        this.records = this.paginator?.pageSize;
        this.dealService.setTablePageSize(JacketType.Deal, this.records);
        this.selectDealType(query, pageSize, offset);
    }

    selectDealType(query?: string, pageSize?: number, offset?: number) {
        this.isLoading = true;

        const sortByMap = {
            status: 'dealStatusId',
            jacketId: 'dealId',
            customer: 'buyer',
            coBuyer: 'coBuyer',
            vehicle: 'vehicle',
            createDate: 'createDate',
        };

        const sortBy = sortByMap[this.sort?.active];

        const sortAsc = this.sort?.direction === 'asc';

        this.dealService
            .getDeals$(this.selectedCategoryFilter, query, pageSize, offset, JacketType.Deal, null, sortBy, sortAsc)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deals: DealData) => {
                scrollIntoView('#query-form');
                this.deals = deals.deals;
                this.dataSource.data = deals.deals;
                this.totalNumberOfDeals = deals.count;
                this.isLoading = false;
            });
    }

    announceSortChange(sortState: Sort) {
        if (sortState.direction) {
            this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
        } else {
            this._liveAnnouncer.announce('Sorting cleared');
        }

        // reset to page 1 when sorting changes

        this.paginator.pageIndex = 0;
        this.selectDealType();
    }

    searchDeals(queryValue?: string) {
        // paginator only exists on page if deals.length > 20
        const offset = this.paginator ? this.paginator.pageIndex * this.paginator.pageSize : 0;
        const pageSize = this.paginator ? this.paginator.pageSize : 25;
        this.isFiltered = queryValue?.length ? true : false;

        this.dealService
            .getDeals$(this.selectedCategoryFilter, queryValue, pageSize, offset, JacketType.Deal)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deals) => {
                this.deals = deals.deals;
                this.dataSource.data = this.deals;
                this.totalNumberOfDeals = deals.count;
            });
    }

    private getCustomerName(jacket: Deal) {
        return !jacket?.buyer?.firstName && !jacket?.buyer?.lastName ? 'the customer' : `${jacket?.buyer?.firstName} ${jacket?.buyer?.lastName}`;
    }

    ngOnDestroy() {
        this.navService.setHelpPanel(null);
        this.isComponentAlive = false;
    }
}
